/**
 * Illustrations de l'application
 *
 * Dessinées en SVG dans le code, pas chargées comme images. Trois raisons, dans
 * cet ordre :
 *
 *  1. **Hors ligne.** L'application doit s'ouvrir sans réseau ; une illustration
 *     en fichier serait un actif de plus à faire entrer dans le service worker,
 *     et un carré vide le jour où il manque.
 *  2. **Thème.** Les couleurs viennent des jetons du système, donc le mode sombre
 *     est juste sans qu'on maintienne une seconde version du dessin.
 *  3. **Poids.** Quelques centaines d'octets contre plusieurs kilo-octets, et
 *     net à toutes les tailles d'écran.
 *
 * Le trait épais et les formes rondes suivent la direction artistique : on
 * cherche l'objet doux, pas l'icône de tableau de bord.
 */

/**
 * Maison au soleil, pour l'écran « tout est à jour ».
 *
 * C'est l'écran qu'on voit le plus souvent quand tout va bien : autant qu'il
 * fasse plaisir plutôt que d'afficher un vide. `aria-hidden` parce que le titre
 * juste à côté dit déjà la même chose — une description alternative ne ferait
 * que répéter.
 */
export function MaisonAuSoleil({ taille = 148 }: { taille?: number }) {
  return (
    <svg
      width={taille}
      height={taille}
      viewBox="0 0 120 120"
      fill="none"
      aria-hidden
      focusable="false"
      style={{ display: 'block', margin: '0 auto' }}
    >
      {/* Soleil : disque plein et rayons courts, en ambre. */}
      <circle cx="92" cy="26" r="12" fill="var(--cat-travaux)" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
        <line
          key={a}
          x1={92 + 17 * Math.cos((a * Math.PI) / 180)}
          y1={26 + 17 * Math.sin((a * Math.PI) / 180)}
          x2={92 + 22 * Math.cos((a * Math.PI) / 180)}
          y2={26 + 22 * Math.sin((a * Math.PI) / 180)}
          stroke="var(--cat-travaux)"
          strokeWidth="3.5"
          strokeLinecap="round"
        />
      ))}

      {/* Sol, en vert. */}
      <path
        d="M6 100 Q 60 92 114 100"
        stroke="var(--cat-courses)"
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />

      {/* Corps de la maison. */}
      <rect x="26" y="56" width="56" height="44" rx="8" fill="var(--surface)" stroke="var(--encre)" strokeWidth="4" />

      {/* Toit, en orange, débordant légèrement. */}
      <path
        d="M20 58 L54 30 L88 58"
        stroke="var(--cat-chauffage)"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />

      {/* Porte, en bleu, et sa poignée. */}
      <rect x="46" y="74" width="18" height="26" rx="5" fill="var(--cat-maison)" />
      <circle cx="59" cy="87" r="1.9" fill="var(--surface)" />

      {/* Fenêtre, avec croisillon. */}
      <rect x="32" y="64" width="12" height="12" rx="3" fill="var(--cat-assainissement)" />

      {/* Deux petites étincelles : le « tout est en ordre ». */}
      <path d="M14 44 l0 -7 M14 44 l0 7 M14 44 l-6 0 M14 44 l6 0" stroke="var(--cat-administratif)" strokeWidth="3" strokeLinecap="round" />
      <path d="M104 72 l0 -5 M104 72 l0 5 M104 72 l-4 0 M104 72 l4 0" stroke="var(--cat-ecole)" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
}
